import { formatMoney } from '@domain';
import type { Lang } from '../i18n/index.js';
import { theme } from '../theme.js';
import type { Presentation, XeroDocument } from './printTypes.js';

/**
 * One company's own invoice design, drawn from their branding record.
 *
 * WHY A SECOND LAYOUT EXISTS
 * A client who has had an invoice design for years does not want a generic one
 * because their bookkeeping moved. Their customers recognise that page. So the
 * layout is a choice per company, and everything that varies inside it — the
 * colours, the slogan, the bank details, the terms — is a field on the record.
 * A component per client would mean new code for every company that has ever
 * had a designer.
 *
 * WHY THE LABELS ARE IN ONE LANGUAGE HERE
 * The plain sheet labels every column in both, because it is ours and it serves
 * two audiences at once. This one is the client's, and their design has one
 * label per column. Doubling them would break the thing we are reproducing. The
 * reader's language decides; the line descriptions still arrive in whatever
 * language Xero holds them.
 */

const DEFAULT_ACCENT = '#9b2242';
const DEFAULT_BAND = '#fbe9cf';

const WORDS = {
  en: {
    invoice: 'INVOICE',
    number: 'Invoice',
    date: 'Date',
    to: 'Invoiced to:',
    description: 'Product Description',
    unit: 'Price / Day',
    qty: 'Days',
    total: 'Total',
    subtotal: 'Subtotal',
    discount: 'Discount',
    tax: 'Tax',
    grand: 'Grand Total',
    payment: 'PAYMENT INFO:',
    bank: 'Bank:',
    accountName: 'ACCOUNT NAME:',
    accountNumber: 'ACCOUNT NUMBER:',
    iban: 'IBAN:',
    terms: 'TERMS & CONDITIONS:',
  },
  ar: {
    invoice: 'فاتورة',
    number: 'رقم الفاتورة',
    date: 'التاريخ',
    to: 'فاتورة إلى:',
    description: 'وصف الخدمة',
    unit: 'السعر / اليوم',
    qty: 'الأيام',
    total: 'الإجمالي',
    subtotal: 'المجموع الفرعي',
    discount: 'الخصم',
    tax: 'الضريبة',
    grand: 'الإجمالي النهائي',
    payment: 'بيانات الدفع:',
    bank: 'البنك:',
    accountName: 'اسم الحساب:',
    accountNumber: 'رقم الحساب:',
    iban: 'الآيبان:',
    terms: 'الشروط والأحكام:',
  },
} as const;

export function BandedSheet({
  doc,
  organisation,
  logoUrl,
  presentation,
  footer,
  lang,
}: {
  doc: XeroDocument | null;
  organisation: string;
  /** Unique per company and per upload, so no cache can confuse two clients. */
  logoUrl: string | null;
  presentation: Presentation;
  footer: { email: string; phone: string; address: string };
  lang: Lang;
}) {
  const w = WORDS[lang === 'ar' ? 'ar' : 'en'];
  const rtl = lang === 'ar';
  const accent = presentation.accentColour || DEFAULT_ACCENT;
  const band = presentation.bandColour || DEFAULT_BAND;
  const tagline = (rtl ? presentation.taglineAr : presentation.taglineEn) || presentation.taglineAr || presentation.taglineEn;

  const lines = doc?.LineItems ?? [];
  const currency = doc?.CurrencyCode ?? '';

  // Xero's SubTotal is already net of any per-line discount, so the discount is
  // what the lines came to before it. Shown only when there is one — an
  // invoice with a zero discount line invites the question "discount off what".
  const gross = lines.reduce((sum, line) => sum + (line.Quantity ?? 0) * (line.UnitAmount ?? 0), 0);
  const subtotal = doc?.SubTotal ?? gross;
  const discount = Math.round((gross - subtotal) * 100) / 100;

  const money = (value: number | undefined) => (value === undefined ? '' : formatMoney(value));

  return (
    <div
      className="sheet"
      dir={rtl ? 'rtl' : 'ltr'}
      style={{
        background: '#fff',
        maxWidth: '820px',
        margin: '0 auto',
        boxShadow: theme.shadowCard,
        borderRadius: '4px',
        overflow: 'hidden',
        fontFamily: rtl ? theme.fontArabic : theme.fontBody,
        color: '#2c2c2c',
        fontSize: '13px',
        lineHeight: 1.6,
      }}
    >
      {/* ---------------------------------------------------------- head band */}
      <div
        className="band"
        style={{
          background: band,
          padding: '22px 38px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '20px',
          minHeight: '92px',
        }}
      >
        {logoUrl ? (
          <img src={logoUrl} alt="" style={{ maxHeight: '86px', maxWidth: '230px', objectFit: 'contain' }} />
        ) : (
          <div style={{ fontSize: '17px', fontWeight: 700, color: accent }}>{organisation}</div>
        )}

        {tagline && (
          <div
            dir={/[؀-ۿ]/.test(tagline) ? 'rtl' : 'ltr'}
            style={{
              color: accent,
              fontWeight: 700,
              fontSize: '21px',
              lineHeight: 1.5,
              textAlign: 'end',
              whiteSpace: 'pre-line',
              maxWidth: '46%',
            }}
          >
            {tagline}
          </div>
        )}
      </div>

      <div style={{ padding: '30px 38px 26px' }}>
        {/* ------------------------------------------------ title and parties */}
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '24px', marginBottom: '30px', flexWrap: 'wrap' }}>
          <div style={{ minWidth: 0, flex: '1 1 300px' }}>
            <div style={{ fontSize: '40px', fontWeight: 800, letterSpacing: '-.01em', lineHeight: 1.05, marginBottom: '12px' }}>
              {w.invoice}
            </div>
            <Underlined label={w.number} value={doc?.InvoiceNumber ?? ''} />
            <Underlined label={w.date} value={(doc?.DateString ?? '').slice(0, 10)} />
          </div>

          <div style={{ flex: '0 1 auto', minWidth: '210px', maxWidth: '46%' }}>
            <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '6px' }}>{w.to}</div>
            <div style={{ fontWeight: 700 }}>{doc?.Contact?.Name ?? ''}</div>
            {doc?.Contact?.EmailAddress && (
              <div dir="ltr" style={{ color: '#666', fontSize: '12px' }}>
                {doc.Contact.EmailAddress}
              </div>
            )}
            {doc?.Reference && <div style={{ color: '#666', fontSize: '12px' }}>{doc.Reference}</div>}
          </div>
        </div>

        {/* ------------------------------------------------------------ lines */}
        <div
          style={{
            background: accent,
            color: '#fff',
            borderRadius: '999px',
            padding: '11px 26px',
            display: 'grid',
            gridTemplateColumns: 'minmax(0,1fr) 110px 70px 110px',
            gap: '10px',
            fontWeight: 600,
            fontSize: '14px',
            marginBottom: '6px',
          }}
        >
          <div>{w.description}</div>
          <div style={{ textAlign: 'center' }}>{w.unit}</div>
          <div style={{ textAlign: 'center' }}>{w.qty}</div>
          <div style={{ textAlign: 'center' }}>{w.total}</div>
        </div>

        {lines.map((line, index) => (
          <div
            key={index}
            className="line"
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0,1fr) 110px 70px 110px',
              gap: '10px',
              padding: '13px 26px',
              borderBottom: '1px solid #cfcfcf',
              alignItems: 'start',
            }}
          >
            <div>{line.Description ?? ''}</div>
            <div dir="ltr" style={{ textAlign: 'center', fontFamily: theme.fontMono }}>
              {money(line.UnitAmount)}
            </div>
            <div dir="ltr" style={{ textAlign: 'center', fontFamily: theme.fontMono }}>
              {line.Quantity ?? ''}
            </div>
            <div dir="ltr" style={{ textAlign: 'center', fontFamily: theme.fontMono }}>
              {money(line.LineAmount)}
            </div>
          </div>
        ))}

        {/* ----------------------------------------------------------- totals */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '18px' }}>
          <div style={{ minWidth: '330px' }}>
            <TotalLine label={w.subtotal} value={money(subtotal)} currency={currency} />
            {discount > 0 && <TotalLine label={w.discount} value={money(discount)} currency={currency} />}
            {typeof doc?.TotalTax === 'number' && doc.TotalTax > 0 && (
              <TotalLine label={w.tax} value={money(doc.TotalTax)} currency={currency} />
            )}

            <div
              style={{
                background: accent,
                color: '#fff',
                borderRadius: '999px',
                padding: '12px 26px',
                display: 'flex',
                justifyContent: 'space-between',
                gap: '16px',
                fontWeight: 700,
                fontSize: '15px',
                marginTop: '10px',
              }}
            >
              <span>{w.grand}</span>
              <span dir="ltr" style={{ fontFamily: theme.fontMono }}>
                {money(doc?.Total)} {currency}
              </span>
            </div>
          </div>
        </div>

        {/* ---------------------------------------------------------- payment */}
        {(presentation.bank.name || presentation.bank.iban || presentation.bank.accountNumber) && (
          <div style={{ marginTop: '26px' }}>
            <div style={{ fontWeight: 700, letterSpacing: '.08em', fontSize: '13px', marginBottom: '8px' }}>
              {w.payment}
            </div>
            <PaymentLine label={w.bank} value={presentation.bank.name} />
            <PaymentLine label={w.accountName} value={presentation.bank.accountName} />
            <PaymentLine label={w.accountNumber} value={presentation.bank.accountNumber} mono />
            <PaymentLine label={w.iban} value={presentation.bank.iban} mono />
          </div>
        )}
      </div>

      {/* --------------------------------------------------------- foot band */}
      {(presentation.terms || footer.email || footer.phone || footer.address) && (
        <div
          className="band"
          style={{
            background: band,
            padding: '24px 38px',
            display: 'flex',
            justifyContent: 'space-between',
            gap: '28px',
            alignItems: 'flex-start',
          }}
        >
          <div style={{ maxWidth: '52%' }}>
            {presentation.terms && (
              <>
                <div style={{ fontWeight: 700, letterSpacing: '.08em', fontSize: '12.5px', marginBottom: '8px' }}>
                  {w.terms}
                </div>
                <div style={{ fontSize: '12px', lineHeight: 1.6, color: '#3a3a3a' }}>{presentation.terms}</div>
              </>
            )}
          </div>

          <div style={{ textAlign: 'end', fontSize: '12px', color: '#3a3a3a', display: 'grid', gap: '7px' }}>
            {footer.email && <span dir="ltr">{footer.email}</span>}
            {footer.phone && (
              <span dir="ltr" style={{ fontFamily: theme.fontMono }}>
                {footer.phone}
              </span>
            )}
            {footer.address && <span>{footer.address}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

/** A label with a ruled line beside it, as on the original design. */
function Underlined({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', marginBottom: '5px', maxWidth: '340px' }}>
      <span style={{ fontWeight: 700, minWidth: '78px' }}>{label}</span>
      <span style={{ fontWeight: 700 }}>:</span>
      <span
        dir="ltr"
        style={{
          borderBottom: '1.5px solid #2c2c2c',
          flex: 1,
          minWidth: '110px',
          fontFamily: theme.fontMono,
          paddingBottom: '2px',
        }}
      >
        {value}
      </span>
    </div>
  );
}

function TotalLine({ label, value, currency }: { label: string; value: string; currency: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', padding: '5px 26px', fontSize: '13.5px' }}>
      <span>{label}</span>
      <span dir="ltr" style={{ fontFamily: theme.fontMono }}>
        {value} {currency}
      </span>
    </div>
  );
}

function PaymentLine({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  if (!value) return null;
  return (
    <div style={{ fontSize: '12.5px', marginBottom: '3px' }}>
      <span style={{ fontWeight: 700 }}>{label} </span>
      <span dir="ltr" style={mono ? { fontFamily: theme.fontMono } : undefined}>
        {value}
      </span>
    </div>
  );
}
